import type AtlasDatePicker from "@/components/form/atlas-date-picker/atlas-date-picker";
import { isEmptyValue } from "@/helpers/validations";
import { getWeekday, Weekday } from "@/internals/date-utils";
import Datepicker from "@/vendors/datepicker-utils";

import FieldValidator, { type FieldValidatorStatus } from "./field-validator";

export default class DateValidator implements FieldValidator {
    public readonly name = "date";

    public status: FieldValidatorStatus = "error";

    private errorType: "none" | "min" | "max" | "both" | "format" | "prevented" = "none";

    private minDate: Date;

    private maxDate: Date;

    private preventedDatesErrorMessage?: string;

    public getInvalidMessage(): string {
        const messageMap = {
            min: () => `A data informada não deve ser menor que ${this.formatDate(this.minDate)}`,
            max: () => `A data informada não deve ser maior que ${this.formatDate(this.maxDate)}`,
            both: () => `Informe uma data entre ${this.formatDate(this.minDate)} e ${this.formatDate(this.maxDate)}`,
            format: () => "Formato de data inválido, utilize o formato DD/MM/AAAA",
            prevented: () => this.preventedDatesErrorMessage || "Esta data não pode ser selecionada"
        };

        return this.errorType === "none" ? "" : messageMap[this.errorType]();
    }

    public validate(element: AtlasDatePicker): boolean {
        const value = element.getElementValue() as string;

        if (!value) return true;

        if (value.length < 10) {
            this.errorType = "format";
            return false;
        }

        this.updateMinAndMax(element);
        this.errorType = "none";

        const valueAsTime = Datepicker.parseDate(value, Datepicker.dateFormat);

        if (this.isPreventedDate(element) || this.isPreventedWeekday(element)) {
            this.errorType = "prevented";
            this.preventedDatesErrorMessage = element.preventedDatesErrorMessage;
        } else if (this.minDate && valueAsTime < this.minDate.getTime()) {
            this.errorType = this.maxDate ? "both" : "min";
        } else if (this.maxDate && valueAsTime > this.maxDate.getTime()) {
            this.errorType = this.minDate ? "both" : "max";
        }

        return this.errorType === "none";
    }

    private updateMinAndMax(element: AtlasDatePicker) {
        const preventPastDate = element.preventPastDate;
        const minDate = element.minDate;

        this.minDate = Datepicker.getDatepickerMinDate(preventPastDate, minDate);

        if (this.minDate) {
            this.minDate.setHours(0, 0, 0, 0);
        }

        const preventLaterDate = element.preventLaterDate;
        const maxDate = element.maxDate;

        this.maxDate = Datepicker.getDatepickerMaxDate(preventLaterDate, maxDate);

        if (this.maxDate) {
            this.maxDate.setHours(23, 59, 59, 999);
        }
    }

    private formatDate(date: Date) {
        return Datepicker.formatDate(date, Datepicker.dateFormat);
    }

    private isPreventedDate(element: AtlasDatePicker): boolean {
        if (isEmptyValue(element.preventDates)) return false;

        const elementValue = element.getElementValue() as string;

        if (!elementValue) return false;

        return element.preventDates.includes(elementValue);
    }

    private isPreventedWeekday(element: AtlasDatePicker): boolean {
        if (isEmptyValue(element.preventWeekdays) && !element.preventWeekends) return false;

        const elementValue = element.getElementValue() as string;

        if (!elementValue) return false;

        const preventWeekdays = new Set(element.preventWeekdays);

        if (element.preventWeekends) {
            preventWeekdays.add(Weekday.SUNDAY);
            preventWeekdays.add(Weekday.SATURDAY);
        }

        const weekday = getWeekday(elementValue);

        return preventWeekdays.has(weekday);
    }
}
